import * as React from 'react';
import { useCallback, useMemo, useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { IOptionType } from '../../../../@types/helper';
import { ITemperatureUnit } from '../../../../@types/model/dispatch/temperatureUnit';
import { IStock } from '../../../../@types/model/stock/stock';
import { IDispatchInstruction } from '../../../../@types/model/dispatch/dispatchInstruction';
import { sum, uniq } from 'lodash';
import { Accordion, AccordionDetails, AccordionSummary, Divider, Icon, IconButton, Tooltip, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PackmanDialog from '../../../../components/dialog/PackmanDialog';
import CustomTooltip from '../../../../components/tooltip/tooltip';
import { CustomTable, PillButton, ControlledTextField, ControlledMuiAutoCompleteSelect } from '@zz2/zz2-ui';
import ControlledDateInput from '../../../../components/input/form/ControlledDateInput';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ITripFormValues, tripFormSchema } from '../../../../@types/model/dispatch/tripFormValues';
import CustomAutoSuggest from '../../../../components/input/CustomAutoSuggest';
import validationService from '../../../../services/validationService';
import CustomTextInput from '../../../../components/input/CustomTextInput';
import SingleToggleButton from '../../../../components/input/SingleToggleButton';
import { v1 as uuidv1 } from 'uuid';
import CustomReactHookFormDevTools from '../../../../components/devtools/ReactHookFormDevTools';

interface ITripFormProps {
    carrierOptions : Array<IOptionType>;
    truckTypeOptions : Array<IOptionType>;
    stocks : Array<IStock>;
    dispatchInstructions : Array<IDispatchInstruction>;
    isEditingTrip : boolean;
    dispatchInstructionStocks : Array<IStock>;
    initialValues : ITripFormValues;
    isLoading ?: boolean;
    onSubmit : (values : ITripFormValues) => void;
    onClose : () => void;
}

const TripForm = (props : ITripFormProps) : React.ReactElement => {
    const schema = tripFormSchema;
    const { initialValues, onSubmit, isLoading } = props;

    // Safe local fallbacks for possibly undefined props
    const carrierOptions = props.carrierOptions ?? [];
    const truckTypeOptions = props.truckTypeOptions ?? [];
    const stocksLocal = props.stocks ?? [];
    const dispatchInstructionsLocal = props.dispatchInstructions ?? [];
    const isEditingTripLocal = props.isEditingTrip ?? false;
    const dispatchInstructionStocksLocal = props.dispatchInstructionStocks ?? [];

    const { handleSubmit, control, formState: { isValid }, watch, setValue, trigger, reset } = useForm<ITripFormValues>({
        defaultValues: initialValues,
        resolver: zodResolver(schema),
        mode: 'onChange',
    });

    // Ensure initial validation runs so isValid reflects default values
    useEffect(() => { void trigger(); }, [trigger, initialValues]);

    const [isDevicesAccordionExpanded, setIsDevicesAccordionExpanded] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    const [editingTemperatureUnit, setEditingTemperatureUnit] = useState<ITemperatureUnit | undefined>(undefined);
    const [editingTemperatureUnitIsActive, setEditingTemperatureUnitIsActive] = useState(true);
    const [selectedStockId, setSelectedStockId] = useState<number | undefined>(undefined);
    const [deviceNumber, setDeviceNumber] = useState<string | undefined>(undefined);
    const [isContainerPopupOpen, setIsContainerPopupOpen] = useState(false);
    const [isDispatchCodesPopupOpen, setIsDispatchCodesPopupOpen] = useState(false);

    const temperatureUnits = watch('temperatureUnits');
    const currentTripId = watch('id');

    const onTareWeightChange = useCallback((e : React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const tareWeight = Number(e.target.value);
        const stockIds : Array<number> = [];
        (dispatchInstructionsLocal).filter(x => x.isActive).forEach(x => x.dispatchLines.filter(y => y.isActive).forEach(y => stockIds.push(y.currentStockId)));
        const stockWeight = sum((stocksLocal).filter(x => x.isActive && uniq(stockIds).some(y => y === x.id)).map(x => x.grossWeight));
        setValue('containerGrossWeight', tareWeight ? (tareWeight + stockWeight) : '', { shouldDirty: true, shouldValidate: true });
    }, [dispatchInstructionsLocal, stocksLocal, setValue]);

    const stockOptions = useMemo(() => {
        const temperatureUnitsLocal = temperatureUnits ?? [];
        if (isEditingTripLocal && dispatchInstructionStocksLocal && dispatchInstructionStocksLocal.length > 0) {
            return dispatchInstructionStocksLocal
                .filter(stock => stock.isActive
                    && ((stock.id === editingTemperatureUnit?.stockId) || !temperatureUnitsLocal.filter((x : ITemperatureUnit) => x.isActive).some((unit : ITemperatureUnit) => unit.stockId === stock.id)))
                .map(x => ({ label: x.barcode, value: x.id }));
        }
        return stocksLocal
            .filter(stock => stock.isActive
                && ((stock.id === editingTemperatureUnit?.stockId) || !temperatureUnitsLocal.filter((x : ITemperatureUnit) => x.isActive).some((unit : ITemperatureUnit) => unit.stockId === stock.id))
                && dispatchInstructionsLocal.some(dispatch => dispatch.isActive && !!dispatch.tripId
                    && (dispatch.tripId === currentTripId)
                    && dispatch.dispatchLines.some(line => line.isActive && line.currentStockId === stock.id)))
            .map(x => ({ label: x.barcode, value: x.id }));
    }, [stocksLocal, dispatchInstructionsLocal, currentTripId, temperatureUnits, editingTemperatureUnit, isEditingTripLocal, dispatchInstructionStocksLocal]);

    const onAddTemperatureUnit = useCallback(() => setIsAdding(true), []);
    const onEditTemperatureUnit = useCallback((unit : ITemperatureUnit) => {
        setEditingTemperatureUnit(unit);
        setSelectedStockId(unit.stockId);
        setDeviceNumber(unit.deviceNumber);
        setEditingTemperatureUnitIsActive(unit.isActive);
    }, []);

    const onDeleteTemperatureUnit = useCallback((temperatureUnit : ITemperatureUnit) => {
        let units = [...(temperatureUnits ?? [])];
        const index = units.findIndex(x => (x.deviceNumber === temperatureUnit.deviceNumber) && (x.stockId === temperatureUnit.stockId));
        const newTempUnit = { ...temperatureUnit };
        newTempUnit.isActive = false;
        units.splice(index, 1, newTempUnit);
        setValue('temperatureUnits', units as any, { shouldDirty: true, shouldValidate: true });
    }, [temperatureUnits, setValue]);

    const onTempUnitDialogClose = useCallback(() => {
        setIsAdding(false);
        setEditingTemperatureUnit(undefined);
    }, []);

    const onTemperatureUnitSubmit = useCallback(() => {
        if (selectedStockId && deviceNumber) {
            let units = [...(temperatureUnits ?? [])];
            let temperatureUnit : ITemperatureUnit;
            if (!!editingTemperatureUnit) {
                temperatureUnit = { ...editingTemperatureUnit } as ITemperatureUnit;
                temperatureUnit.stockId = selectedStockId;
                temperatureUnit.deviceNumber = deviceNumber;
                temperatureUnit.isActive = editingTemperatureUnitIsActive;
                temperatureUnit.tripId = currentTripId;
                const index = units.findIndex(x => (x.deviceNumber === (editingTemperatureUnit as any)?.deviceNumber) && (x.stockId === (editingTemperatureUnit as any)?.stockId));
                units.splice(index, 1, temperatureUnit as any);
            } else {
                temperatureUnit = {
                    id: 0,
                    stockId: selectedStockId,
                    deviceNumber,
                    tripId: currentTripId,
                    guid: uuidv1(),
                    isActive: true,
                } as any;
                units = [...units, temperatureUnit as any];
            }
            setValue('temperatureUnits', units as any, { shouldDirty: true, shouldValidate: true });
            onTempUnitDialogClose();
        }
    }, [selectedStockId, deviceNumber, editingTemperatureUnit, editingTemperatureUnitIsActive, currentTripId, temperatureUnits, setValue, onTempUnitDialogClose]);

    const toggleDevicesAccordion = useCallback(() => setIsDevicesAccordionExpanded(x => !x), []);
    const openContainerNumberInfoPopup = useCallback(() => setIsContainerPopupOpen(true), []);
    const closeContainerNumberInfoPopup = useCallback(() => setIsContainerPopupOpen(false), []);
    const openDispatchCodesPopup = useCallback(() => setIsDispatchCodesPopupOpen(true), []);
    const closeDispatchCodesPopup = useCallback(() => setIsDispatchCodesPopupOpen(false), []);

    const isTripLinkedToDispatch = useCallback((tripId : number) => {
        const dispatches = (dispatchInstructionsLocal).filter(x => x.isActive && x.tripId === tripId);
        return !(dispatches.length > 0);
    }, [dispatchInstructionsLocal]);

    const getDispatchesLinkedToTrip = useCallback((tripId : number) => {
        return (dispatchInstructionsLocal).filter(x => x.isActive && x.tripId === tripId);
    }, [dispatchInstructionsLocal]);

    const getStockBarcode = useCallback((stockId : number) => (stocksLocal).find(x => x.id === stockId)?.barcode, [stocksLocal]);

    const temperatureUnitColumns = useMemo(() => [
        { title: 'Barcode', field: 'stockId', formatFunction: getStockBarcode },
        { title: 'Device Number', field: 'deviceNumber' },
    ], [getStockBarcode]);

    const onClear = useCallback(() => {
        reset(initialValues);
        void trigger();
    }, [reset, initialValues, trigger]);

    return (
        <form onSubmit={handleSubmit(onSubmit)} className={'p20 wfill'}>
            <CustomReactHookFormDevTools control={control} />
            <div className={'fdc'}>
                <Typography variant='subtitle2' className={'fw600 mb5'}>{'Trip'}</Typography>
                <Divider className={'mb10'} />
                <div className={'fdr flx1'}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <ControlledDateInput name={'loadDate'} label={'Load Date'} control={control} defaultValue={initialValues.loadDate as unknown as Date} className={'mt0 flx1'} type={'date-fns'} />
                    </LocalizationProvider>
                    <div className={'w20'} />
                    <ControlledTextField name={'description'} label={'Description'} control={control} defaultValue={initialValues.description} className={'mt0 flx1'} />
                </div>
                <div className={'fdr flx1 mt10'}>
                    <div className={'flx1'}>
                        <SingleToggleButton name={'isActive'} label={'Is Active'} onChange={() => setValue('isActive', !(watch('isActive')), { shouldDirty: true })} value={watch('isActive')} />
                    </div>
                    <div className={'w20'} />
                    <div className={'flx1'} />
                </div>
                <Typography variant='subtitle2' className={'fw600 mt20 mb5'}>{'Vehicle'}</Typography>
                <Divider className={'mb10'} />
                <div className={'fdr flx1 mt10'}>
                    <ControlledTextField name={'driver'} label={'Driver'} control={control} defaultValue={initialValues.driver} className={'flx1'} />
                    <div className={'w20'} />
                    <ControlledTextField name={'registrationNumber'} label={'Registration Number'} control={control} defaultValue={initialValues.registrationNumber} className={'flx1'} />
                </div>
                <div className={'fdr flx1 mt10'}>
                    <ControlledMuiAutoCompleteSelect name={'carrier'} label={'Carrier'} className={'flx1 h50'} control={control} options={carrierOptions} defaultValues={initialValues.carrier ?? null} />
                    <div className={'w20'} />
                    <ControlledMuiAutoCompleteSelect name={'truckType'} label={'Truck Type'} className={'flx1 h50'} control={control} options={truckTypeOptions} defaultValues={initialValues.truckType ?? null} />
                </div>
                <div className={'fdr flx1 mt10'}>
                    <ControlledTextField name={'fleetNumber'} label={'Fleet Number'} control={control} defaultValue={initialValues.fleetNumber} className={'flx1'} />
                    <div className={'w20'} />
                    <div className={'flx1'} />
                </div>
                <Typography variant='subtitle2' className={'fw600 mt20 mb5'}>{'Container'}</Typography>
                <Divider className={'mb10'} />
                <div className={'fdr flx1 mt10'}>
                    <div className='fdr aic flx1'>
                        <ControlledTextField name={'container'} label={'Container'} control={control} defaultValue={initialValues.container} className={'flx1'} />
                        <CustomTooltip title={'Container Number Info'}>
                            <IconButton className={'ml5 h30 w30'} onClick={openContainerNumberInfoPopup}>
                                <Icon fontSize={'small'}>info</Icon>
                            </IconButton>
                        </CustomTooltip>
                    </div>
                    <div className={'w20'} />
                    <ControlledTextField name={'sealNumber'} label={'Seal Number'} control={control} defaultValue={initialValues.sealNumber} className={'flx1'} />
                </div>
                <div className={'fdr flx1 mt10'}>
                    <ControlledTextField name={'containerTareWeight'} label={'Container Tare Weight'} control={control} defaultValue={(initialValues as any).containerTareWeight as any} className={'flx1'} onChange={onTareWeightChange as any} />
                    <div className={'w20'} />
                    <ControlledTextField name={'containerGrossWeight'} label={'Container Gross Weight'} control={control} defaultValue={(initialValues as any).containerGrossWeight as any} className={'flx1'} disabled />
                </div>
            </div>

            {!!currentTripId && (
                <>
                    <Tooltip disableInteractive title={!isTripLinkedToDispatch(currentTripId) ? '' : 'No dispatches linked to trip'}>
                        <div>
                            <Accordion key={'trip_devices'} className={'fdc wfill mb10'} expanded={!!isDevicesAccordionExpanded} disabled={currentTripId > 0 ? isTripLinkedToDispatch(currentTripId) : false} onChange={toggleDevicesAccordion}>
                                <AccordionSummary className={'aic jcc bt1 cgray3 bb1'} expandIcon={<ExpandMoreIcon className={'cw'} />}>
                                    <Typography>{'Devices'}</Typography>
                                </AccordionSummary>
                                <AccordionDetails className={'fdc hfill wfill'}>
                                    <div className={'fdc'}>
                                        <CustomTable<ITemperatureUnit>
                                            enableAdding={true}
                                            addFunction={onAddTemperatureUnit}
                                            editFunction={onEditTemperatureUnit}
                                            enableEditing={(row : ITemperatureUnit) => !(temperatureUnits ?? []).filter((x : ITemperatureUnit) => x.isActive && x.id !== row.id).some((x : ITemperatureUnit) => x.stockId === row.stockId)}
                                            fitWidthToPage
                                            enableDeleting={(row : ITemperatureUnit) => row.isActive}
                                            deleteFunction={onDeleteTemperatureUnit}
                                            columns={temperatureUnitColumns}
                                            rows={(temperatureUnits as any) ?? []}
                                            initialSortOrder={[{ columnName: 'stockId_Barcode', direction: 'desc' }]}
                                            isActive={(row : ITemperatureUnit) => row.isActive}
                                        />
                                    </div>
                                </AccordionDetails>
                            </Accordion>
                        </div>
                    </Tooltip>
                    {(isAdding || !!editingTemperatureUnit) && (
                        <PackmanDialog maxWidth={'md'} isOpen={isAdding || !!editingTemperatureUnit} title={'Temperature Unit'} isEdit={!!editingTemperatureUnit} onClose={onTempUnitDialogClose}>
                            <div className={'fdr p10'}>
                                <CustomAutoSuggest className={'flx1'} options={stockOptions} label={'Stock'} initialValue={editingTemperatureUnit?.stockId} hasInitialValue={!!editingTemperatureUnit?.stockId} value={editingTemperatureUnit?.stockId} validate={validationService.required} validateOnBlur validateOnChange field={'stockbarcode'} onChange={(e : any) => setSelectedStockId(e)} enableDropDownIcon />
                                <div className={'w20'} />
                                <CustomTextInput className={'flx1'} field={'deviceNumber'} label={'Device Number'} initialValue={editingTemperatureUnit?.deviceNumber ?? ''} onChange={(e : React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement | HTMLSelectElement>) => setDeviceNumber(e.target.value)} validate={validationService.required} validateOnBlur validateOnChange />
                            </div>
                            <div className={'fdr'}>
                                <SingleToggleButton className={'w200'} label={'Is Active'} onChange={() => setEditingTemperatureUnitIsActive(x => !x)} name='isActive' value={editingTemperatureUnitIsActive} />
                                <div className={'w20'} />
                                <div className={'flx1'} />
                            </div>
                            <div className={'fdr ml10 ais jcfe pt10 pb10 pr10'}>
                                <Button className={'fwb h35'} variant='text' color='primary' type={'reset'}>
                                    Clear
                                </Button>
                                <PillButton disabled={!selectedStockId || !deviceNumber} className={'ml15 pl20 pr20 h35'} text={'Save'} onClick={onTemperatureUnitSubmit} color={'secondary'} />
                            </div>
                        </PackmanDialog>
                    )}
                </>
            )}
            {!!isContainerPopupOpen && (
                <PackmanDialog maxWidth={'md'} isOpen={isContainerPopupOpen} title={'Container Number Info'} isInfo onClose={closeContainerNumberInfoPopup}>
                    <div className={'fdc p20'}>
                        <img className={'hi wi mb10'} src={`${ASSET_BASE}/assets/images/container-id-number.png`} />
                        <div className={'fdr'}>
                            <Typography>{'For more info click: '}</Typography>
                            <div className={'w10'} />
                            <a className='pt2' href={'https://www.bic-code.org/identification-number/'}>https://www.bic-code.org/identification-number/</a>
                        </div>
                    </div>
                </PackmanDialog>
            )}
            {!!isDispatchCodesPopupOpen && (
                <PackmanDialog maxWidth={'md'} isOpen={isDispatchCodesPopupOpen} title={'Linked Dispatch Codes'} isInfo onClose={closeDispatchCodesPopup}>
                    <div className={'fdc p10 oya h300'}>
                        {getDispatchesLinkedToTrip(currentTripId).map(dispatch => (
                            <div className={'fdc'}>
                                <div className={'fdr jcc fw600'}>{dispatch.dispatchCode}</div>
                                <Divider className={'wfill mt5 mb5'} />
                            </div>
                        ))}
                    </div>
                </PackmanDialog>
            )}
            <div className={'fdr ml10 ais jcsb pt20 pb10'}>
                <div className={'fdr aic'}>
                    <PillButton className={!isTripLinkedToDispatch(currentTripId) ? 'h35' : 'dn'} text={'Linked Dispatches'} onClick={openDispatchCodesPopup} color={'secondary'} />
                </div>
                <div className={'fdr'}>
                    <Button className={'fwb h35'} variant='text' color='primary' type={'button'} onClick={onClear}>Clear</Button>
                    <PillButton disabled={!isValid || !!isLoading} className={'ml15 pl20 pr20 h35'} text={'Save'} type={'submit'} color={'secondary'} />
                </div>
            </div>
        </form>
    );
};

export default TripForm;
